import React, { useState } from 'react';
import { 
  MapPinIcon, 
  XMarkIcon,
  LightBulbIcon,
  CodeBracketIcon,
  CloudArrowDownIcon,
  CpuChipIcon,
  ArrowUpRightIcon,
  ArrowTrendingUpIcon,
  ChevronDownIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { RocketLaunchIcon, BoltIcon } from '@heroicons/react/24/solid';

export default function Search() {
  const [searchDepth, setSearchDepth] = useState('Standard');

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 px-6 py-12 overflow-y-auto">
        <div className="max-w-[1100px] mx-auto flex flex-col items-center">
          
          {/* Hero Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-100 px-4 py-1.5 rounded-full mb-6">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-[11px] font-bold text-brand-600 uppercase tracking-widest">Our Search Engine is Active</span>
            </div>
            <h1 className="text-[48px] md:text-[56px] font-extrabold text-slate-900 tracking-tight mb-4 leading-none">
              Find Decision Makers.
            </h1>
            <p className="text-[18px] md:text-[20px] text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">
              Real-time AI data analysis across the live web. High-precision intelligence for the modern enterprise.
            </p>
          </div>

          {/* Main Search Card */}
          <div className="w-full bg-white border border-slate-200 rounded-[32px] p-8 shadow-xl shadow-slate-200/50 mb-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">
              
              {/* Company Name Input */}
              <div className="md:col-span-5 relative">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">Company Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Anthropic, Stripe" 
                  className="w-full text-lg font-medium text-slate-900 placeholder:text-slate-300 border-none p-0 focus:ring-0"
                />
              </div>

              <div className="hidden md:block w-px h-12 bg-slate-100 self-end mb-2"></div>

              {/* Location Input */}
              <div className="md:col-span-3 relative">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">Location</label>
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-5 h-5 text-slate-400" />
                  <input 
                    type="text" 
                    placeholder="Global" 
                    className="w-full text-lg font-medium text-slate-900 placeholder:text-slate-300 border-none p-0 focus:ring-0"
                  />
                </div>
              </div>

              <div className="hidden md:block w-px h-12 bg-slate-100 self-end mb-2"></div>

              {/* Leadership Titles (Pills) */}
              <div className="md:col-span-3">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">Leadership Titles</label>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-full text-sm font-bold text-slate-700 border border-slate-200">
                    CEO <button className="hover:text-slate-900"><XMarkIcon className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-full text-sm font-bold text-slate-700 border border-slate-200">
                    Founder <button className="hover:text-slate-900"><XMarkIcon className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="text-xs font-bold text-slate-400">+3</span>
                </div>
              </div>
            </div>

            {/* Bottom Controls of Search Card */}
            <div className="flex flex-col md:flex-row items-center justify-between pt-6 border-t border-slate-100 gap-6">
              
              <div className="flex items-center gap-6 w-full md:w-auto">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Search Depth:</span>
                  <div className="flex bg-slate-100 p-1 rounded-full">
                    {['Standard', 'Deep', 'Super Deep'].map((depth) => (
                      <button 
                        key={depth}
                        onClick={() => setSearchDepth(depth)}
                        className={`px-4 py-1.5 rounded-full text-[12px] font-bold transition-all ${
                          searchDepth === depth 
                            ? 'bg-white text-slate-900 shadow-sm' 
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {depth}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="hidden md:flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                  <span className="w-3.5 h-3.5 border border-slate-300 rounded-full flex items-center justify-center text-[8px]">i</span>
                  Estimated Cost: 1 Credit
                </div>
              </div>

              <button className="w-full md:w-auto bg-brand-500 text-white px-8 py-3.5 rounded-full text-[15px] font-bold flex items-center justify-center gap-2 hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/25">
                Find Decision Makers <BoltIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Precision Tip Bubble */}
          <div className="bg-white border border-slate-200 rounded-full px-6 py-3 shadow-md flex items-center gap-3 mb-16 relative z-0 -mt-10 pt-8 pb-3 max-w-fit">
            <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center">
              <LightBulbIcon className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-[13px] text-slate-600"><strong className="text-slate-900 font-bold">Precision Tip:</strong> Use Deep Search for more accurate results.</p>
          </div>

          {/* Bottom Layout Grid */}
          <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 relative">
            
            {/* Background connecting curve (Stylistic only) */}
            <div className="absolute top-0 left-1/3 w-32 h-32 bg-slate-100 rounded-full blur-3xl -z-10"></div>

            {/* Left Column: Live Pipeline */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-widest mb-2 pl-2">Live Pipeline</h3>
              
              {/* Pipeline Steps */}
              <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm">
                
                {/* Step 1 */}
                <div className="flex items-center gap-5 mb-8 opacity-100">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center shrink-0 border border-brand-100">
                    <CodeBracketIcon className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[13px] font-bold text-slate-900 uppercase tracking-widest">Query Generation</h4>
                      <span className="text-[11px] font-bold text-brand-600">Active</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="w-full h-full bg-brand-500 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-center gap-5 mb-8 opacity-60">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 border border-slate-100">
                    <CloudArrowDownIcon className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[13px] font-bold text-slate-900 uppercase tracking-widest">Smart Retrieval</h4>
                      <span className="text-[11px] font-bold text-slate-400">Waiting</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="w-[30%] h-full bg-slate-400 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-center gap-5 opacity-30">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 border border-slate-100">
                    <CpuChipIcon className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[13px] font-bold text-slate-400 uppercase tracking-widest">AI Extraction</h4>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden"></div>
                  </div>
                </div>

              </div>

              {/* Mini Chart */}
              <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[13px] font-bold text-slate-900 uppercase tracking-widest">Search Velocity</h4>
                  <ArrowTrendingUpIcon className="w-4 h-4 text-slate-400" />
                </div>
                <p className="text-[11px] font-bold text-emerald-500 mb-6">+12% from yesterday</p>
                
                {/* Simple SVG Sparkline */}
                <div className="w-full h-12 mb-4">
                  <svg className="w-full h-full overflow-visible" viewBox="0 0 100 20" preserveAspectRatio="none">
                    <path d="M0,15 L15,15 L25,12 L40,16 L55,10 L70,12 L85,5 L100,8" fill="none" stroke="#0054D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="85" cy="5" r="2" fill="#0054D6" />
                  </svg>
                </div>
                <div className="flex justify-between text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                  <span>08:00</span><span>12:00</span><span>16:00</span><span>20:00</span>
                </div>
              </div>

            </div>

            {/* Right Column: Recent Searches */}
            <div className="lg:col-span-8 flex flex-col gap-6">
              <div className="flex items-center justify-between pl-2">
                <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Recent Searches From Your Account</h3>
                <button className="text-[11px] font-bold text-brand-600 hover:text-brand-800 uppercase tracking-widest flex items-center gap-1">
                  View Logs <ArrowUpRightIcon className="w-3 h-3" />
                </button>
              </div>

              <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
                <div className="grid grid-cols-3 px-8 py-5 border-b border-slate-100 bg-slate-50/50 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                  <div>List Name</div>
                  <div>Title & Company</div>
                  <div className="text-right">Credits Used</div>
                </div>

                <div className="flex flex-col">
                  {/* Item 1 */}
                  <div className="grid grid-cols-3 items-center px-8 py-6 border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-teal-100 border-2 border-white flex items-center justify-center text-teal-800 font-bold overflow-hidden">
                           <img src="https://ui-avatars.com/api/?name=Elena+Rodriguez&background=0D8ABC&color=fff" alt="Avatar" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-brand-50 rounded-full flex items-center justify-center border border-white">
                          <CodeBracketIcon className="w-3 h-3 text-brand-500" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Elena Rodriguez</h4>
                        <p className="text-[12px] text-slate-500">Extracted 2m ago</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Co-Founder & CTO</h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Aether Systems</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 bg-emerald-50 text-emerald-600 text-[12px] font-bold rounded-full">
                        50
                      </span>
                    </div>
                  </div>

                  {/* Item 2 */}
                  <div className="grid grid-cols-3 items-center px-8 py-6 border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center text-indigo-800 font-bold overflow-hidden">
                          <img src="https://ui-avatars.com/api/?name=Marcus+Chen&background=1e293b&color=fff" alt="Avatar" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-50 rounded-full flex items-center justify-center border border-white">
                          <CloudArrowDownIcon className="w-3 h-3 text-red-500" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Marcus Chen</h4>
                        <p className="text-[12px] text-slate-500">Extracted 15m ago</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">CEO</h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Lumina Health</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 bg-brand-50 text-brand-600 text-[12px] font-bold rounded-full">
                        80
                      </span>
                    </div>
                  </div>

                  {/* Item 3 */}
                  <div className="grid grid-cols-3 items-center px-8 py-6 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-slate-600 font-bold">
                          SD
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center border border-white">
                          <DocumentTextIcon className="w-3 h-3 text-slate-500" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Sarah Davies</h4>
                        <p className="text-[12px] text-slate-500">Extracted 1h ago</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Managing Director</h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Veritas Capital</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center justify-center px-3 py-1 bg-amber-50 text-amber-600 text-[12px] font-bold rounded-full">
                        100
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-8 py-5 border-t border-slate-100 flex justify-center bg-slate-50/50">
                  <button className="text-[11px] font-bold text-slate-500 uppercase tracking-widest hover:text-slate-900 transition-colors flex items-center gap-2">
                    Load Full Results <ChevronDownIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}